import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/useAuth';
import { contentHref } from '@/lib/contentHref';
import { removeBookmark, subscriberQueries, updateBookmarkNote } from '@/lib/queries/subscriber';

export const Route = createFileRoute('/subscriber/saved')({
  component: SavedRoute,
});

function SavedRoute() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const bookmarks = useQuery(subscriberQueries.bookmarks(user?.id));
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const items = bookmarks.data ?? [];

  const saveNote = async (contentId: string) => {
    if (!user) return;
    try {
      await updateBookmarkNote(user.id, contentId, draft);
      await queryClient.invalidateQueries({ queryKey: ['bookmarks', user.id] });
      setEditing(null);
      toast.success('Note saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save note');
    }
  };

  const remove = async (contentId: string) => {
    if (!user) return;
    try {
      await removeBookmark(user.id, contentId);
      await queryClient.invalidateQueries({ queryKey: ['bookmarks', user.id] });
      toast.success('Removed from saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove from saved');
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-serif text-3xl text-ink">Saved</h1>
      {bookmarks.isLoading ? (
        <p className="mt-12 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-12 text-center font-sans text-sm text-ink/50">
          Nothing saved yet. Use the bookmark icon on any piece to save it for later.
        </p>
      ) : (
        <ul className="mt-8 space-y-4">
          {items.map((b) => (
            <li
              key={b.content_id}
              className="relative rounded-lg border border-ink/10 bg-parchment p-5"
            >
              {/* The card is the primary link (Spec 27 §6): the title's stretched
                  ::after overlay makes the whole card navigate to the piece —
                  type-aware via contentHref, so a saved video routes right. The
                  Remove button and note control sit above it (z-10) and
                  stopPropagation, so they never trigger navigation. */}
              <div className="flex items-start justify-between gap-4">
                <Link
                  to={contentHref(b.content?.type ?? 'article', b.content?.slug ?? '')}
                  className="font-serif text-lg text-ink after:absolute after:inset-0 after:content-[''] hover:text-oxblood"
                >
                  {b.content?.title ?? 'Untitled'}
                </Link>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(b.content_id);
                  }}
                  className="relative z-10 shrink-0 rounded border border-signal/30 px-2 py-1 font-sans text-xs text-signal/80 hover:bg-signal/10"
                >
                  Remove
                </button>
              </div>
              {b.content?.excerpt && (
                <p className="mt-2 line-clamp-2 font-sans text-sm text-ink/60">
                  {b.content.excerpt}
                </p>
              )}
              {editing === b.content_id ? (
                <div className="relative z-10 mt-3">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => void saveNote(b.content_id)}
                      className="rounded-md bg-ink px-3 py-1 text-xs font-medium text-parchment hover:bg-oxblood"
                    >
                      Save note
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="rounded-md border border-ink/20 px-3 py-1 text-xs text-ink"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(b.content_id);
                    setDraft(b.note ?? '');
                  }}
                  className="relative z-10 mt-3 block text-left font-sans text-sm italic text-ink/60 hover:text-oxblood"
                >
                  {b.note ? `“${b.note}”` : 'Add a note…'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
